import React from 'react';
import { useTranslation } from '../context/LanguageContext';
import { AppointmentResponse, AppointmentStatus } from '../types/api';
import './ExpandedDatePicker.css';

interface ExpandedDatePickerProps {
  selectedDate: string;
  onDateChange: (date: string) => void;
  minDate?: string;
  maxDate?: string;
  disabled?: boolean;
  appointments?: AppointmentResponse[];
}

const ExpandedDatePicker: React.FC<ExpandedDatePickerProps> = ({
  selectedDate,
  onDateChange,
  minDate,
  maxDate,
  disabled = false,
  appointments = []
}) => {
  const { t } = useTranslation();

  const parseDate = (dateString: string) => {
    // Parse date in local timezone to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };
  const formatDateString = (date: Date) => {
    // Format date in local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Separate calendar view state from selected date
  const [viewDate, setViewDate] = React.useState(() => parseDate(selectedDate));
  const currentMonth = viewDate.getMonth();
  const currentYear = viewDate.getFullYear();

  // Sync view date when selected date changes externally
  React.useEffect(() => {
    const selectedDateObj = parseDate(selectedDate);
    setViewDate(selectedDateObj);
  }, [selectedDate]);

  const minDateObj = minDate ? parseDate(minDate) : null;
  const maxDateObj = maxDate ? parseDate(maxDate) : null;

  // Helper function to get Monday-based day index (Monday = 0, Sunday = 6)
  const getMondayBasedDayIndex = (date: Date): number => {
    const day = date.getDay();
    return day === 0 ? 6 : day - 1; // Convert Sunday from 0 to 6, others shift down by 1
  };

  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const startDate = new Date(firstDayOfMonth);
  // Calculate days to subtract to get to the Monday of the week containing the first day
  const daysToSubtract = getMondayBasedDayIndex(firstDayOfMonth);
  startDate.setDate(startDate.getDate() - daysToSubtract);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const hasAppointment = (date: Date): AppointmentResponse | undefined => {
    const dateString = formatDateString(date);
    const found = appointments.find(apt => {
      // Handle different date formats - API returns ISO format like '2025-07-12T00:00:00Z'
      const aptDateOnly = apt.appointmentDate.split('T')[0]; // Extract just the date part
      return aptDateOnly === dateString && apt.status === AppointmentStatus.Active;
    });
    return found;
  };

  const handleDateClick = (date: Date) => {
    if (disabled) return;
    
    const dateString = formatDateString(date);
    
    if (minDateObj && date < minDateObj) return;
    if (maxDateObj && date > maxDateObj) return;
    
    // Update view date to the month of the clicked date
    if (date.getMonth() !== currentMonth || date.getFullYear() !== currentYear) {
      setViewDate(new Date(date.getFullYear(), date.getMonth(), 1));
    }
    
    onDateChange(dateString);
  };

  const handlePrevMonth = () => {
    if (disabled) return;
    const newViewDate = new Date(currentYear, currentMonth - 1, 1);
    setViewDate(newViewDate);
  };

  const handleNextMonth = () => {
    if (disabled) return;
    const newViewDate = new Date(currentYear, currentMonth + 1, 1);
    setViewDate(newViewDate);
  };

  const renderCalendarDays = () => {
    const elements = [];
    const currentDate = new Date(startDate);
    let currentRow = [];

    for (let i = 0; i < 42; i++) {
      const date = new Date(currentDate);
      const isCurrentMonth = date.getMonth() === currentMonth;
      const isSelected = formatDateString(date) === selectedDate;
      const isToday = formatDateString(date) === formatDateString(new Date());
      const appointment = hasAppointment(date);
      const hasAppointmentBooked = !!appointment;
      const isDisabled = Boolean(
        disabled ||
        (minDateObj && date < minDateObj) ||
        (maxDateObj && date > maxDateObj)
      );

      // Check if this is the 1st of a month and we're not at the start of a row
      const isFirstOfMonth = date.getDate() === 1;
      const shouldBreakRow = isFirstOfMonth && currentRow.length > 0;

      // If we need to break the row, finish the current row with empty spaces
      if (shouldBreakRow) {
        // Fill the rest of the current row with empty spaces
        while (currentRow.length < 7) {
          currentRow.push(<div key={`empty-${elements.length}-${currentRow.length}`} className="calendar-day-empty"></div>);
        }
        
        // Add the completed row
        elements.push(
          <div key={`row-${elements.length}`} className="calendar-week">
            {currentRow}
          </div>
        );
        
        // Start a new row and add empty spaces to align the 1st with correct weekday
        currentRow = [];
        
        // Calculate which day of the week this 1st should appear on
        const dayOfWeek = getMondayBasedDayIndex(date); // 0 = Monday, 6 = Sunday
        
        // Add empty spaces before the 1st to align it correctly
        for (let j = 0; j < dayOfWeek; j++) {
          currentRow.push(<div key={`empty-before-${elements.length}-${j}`} className="calendar-day-empty"></div>);
        }
      }

      // Add the current day to the row
      currentRow.push(
        <button
          key={`day-${i}`}
          type="button"
          className={`calendar-day ${
            isCurrentMonth ? 'current-month' : 'other-month'
          } ${isSelected ? 'selected' : ''} ${
            isToday ? 'today' : ''
          } ${isDisabled ? 'disabled' : ''} ${
            hasAppointmentBooked ? 'has-appointment' : ''
          }`}
          onClick={() => handleDateClick(date)}
          disabled={isDisabled}
          title={hasAppointmentBooked ? `${t('appointment', 'Appointment')}: ${appointment?.startTime} - ${appointment?.endTime}` : undefined}
        >
          {date.getDate()}
          {hasAppointmentBooked && (
            <div className="appointment-indicator">
              <div className="appointment-dot"></div>
            </div>
          )}
        </button>
      );

      // If we've filled a row (7 days), add it to elements
      if (currentRow.length === 7) {
        elements.push(
          <div key={`row-${elements.length}`} className="calendar-week">
            {currentRow}
          </div>
        );
        currentRow = [];
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Add any remaining days in the last row
    if (currentRow.length > 0) {
      // Fill with empty spaces if needed
      while (currentRow.length < 7) {
        currentRow.push(<div key={`empty-final-${currentRow.length}`} className="calendar-day-empty"></div>);
      }
      
      elements.push(
        <div key={`row-${elements.length}`} className="calendar-week">
          {currentRow}
        </div>
      );
    }

    return elements;
  };

  return (
    <div className="expanded-date-picker">
      <div className="calendar-header">
        <button
          type="button"
          className="nav-button"
          onClick={handlePrevMonth}
          disabled={disabled}
          title={t('previousMonth', 'Previous Month')}
        >
          ←
        </button>
        <span className="month-year">
          {monthNames[currentMonth]} {currentYear}
        </span>
        <button
          type="button"
          className="nav-button"
          onClick={handleNextMonth}
          disabled={disabled}
          title={t('nextMonth', 'Next Month')}
        >
          →
        </button>
      </div>
      
      <div className="calendar-grid">
        <div className="day-headers">
          {dayNames.map(day => (
            <div key={day} className="day-header">
              {day}
            </div>
          ))}
        </div>
        <div className="calendar-days">
          {renderCalendarDays()}
        </div>
      </div>
    </div>
  );
};

export default ExpandedDatePicker;